import { useEffect, useState } from 'react';
import useFirebase from '@app/providers/firebase';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';

type UseMarkdownResult = {
  content: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  resolvedUrl?: string | null;
};

const markdownCache = new Map<string, string>();

export function useMarkdown(url: string | undefined | null): UseMarkdownResult {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { storage } = useFirebase();
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    const resolveDownloadUrl = async (input: string): Promise<string> => {
      if (!input) return input;
      // Allow relative project-style paths like "/docs/..." mapped to Firebase Storage bucket root
      if (input.startsWith('/')) {
        const pathInBucket = input.replace(/^\//, '');
        return await getDownloadURL(storageRef(storage, pathInBucket));
      }
      // Support gs:// or Firebase https URLs via SDK to respect security rules
      if (input.startsWith('gs://') || input.startsWith('https://firebasestorage.googleapis.com/')) {
        return await getDownloadURL(storageRef(storage, input));
      }
      // Otherwise assume it's a normal https URL
      return input;
    };

    const load = async () => {
      if (!url) {
        setContent(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const downloadUrl = await resolveDownloadUrl(url);
        setResolvedUrl(downloadUrl);
        const shouldBypassCache = import.meta.env.DEV || refetchTrigger > 0;

        if (!shouldBypassCache && markdownCache.has(downloadUrl)) {
          if (!isCancelled) setContent(markdownCache.get(downloadUrl) ?? null);
          if (!isCancelled) setIsLoading(false);
          return;
        }

        // Clear cache entry on refetch
        if (refetchTrigger > 0) {
          markdownCache.delete(downloadUrl);
        }

        const cacheParam = shouldBypassCache ? `${downloadUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : '';
        const urlForRequest = `${downloadUrl}${cacheParam}`;
        const requestUrl = import.meta.env.DEV && urlForRequest.startsWith('https://firebasestorage.googleapis.com')
          ? `/__md${urlForRequest.replace('https://firebasestorage.googleapis.com', '')}`
          : urlForRequest;

        const response = await fetch(requestUrl, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Failed to load markdown: ${response.status} ${response.statusText}`);
        }
        const text = await response.text();
        if (!shouldBypassCache) markdownCache.set(downloadUrl, text);
        if (!isCancelled) setContent(text);
      } catch (err) {
        if (!isCancelled) setError(err as Error);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    void load();
    return () => { isCancelled = true; };
  }, [url, storage, refetchTrigger]);

  const refetch = () => {
    // Trigger re-fetch by incrementing the trigger
    setRefetchTrigger((prev) => prev + 1);
  };

  return { content, isLoading, error, refetch, resolvedUrl };
}

export default useMarkdown;


